import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Search, ShieldAlert, Bot, Link2, LineChart, FileText, Sparkles, LogOut, Menu, X, Zap, Gift, CreditCard, Settings as SettingsIcon, Plus, Pencil, MailWarning, Loader2, BookOpen, MapPin } from "lucide-react";
import { ProjectManagerModal } from "@/components/ProjectManagerModal";
import AnnualUpsellModal from "@/components/AnnualUpsellModal";
import { Logo } from "@/components/Logo";
import { UsageMeter } from "@/components/UsageMeter";
import { UpgradeNagModal } from "@/components/UpgradeNagModal";
import { OnboardingTour } from "@/components/OnboardingTour";
import { AccessibilityZoom } from "@/components/AccessibilityZoom";
import { toast } from "sonner";

const ProjectCtx = { current: null };

export function useProject() {
    // simple pass-through via localStorage
    const [proj, setProj] = useState(() => {
        try { return JSON.parse(localStorage.getItem("rf_project")); } catch { return null; }
    });
    useEffect(() => {
        const handler = () => {
            try { setProj(JSON.parse(localStorage.getItem("rf_project"))); } catch { setProj(null); }
        };
        window.addEventListener("rf_project_changed", handler);
        return () => window.removeEventListener("rf_project_changed", handler);
    }, []);
    return proj;
}

const NAV = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/app/keywords", label: "Keywords", icon: Search, testid: "nav-keywords" },
    { to: "/app/audit", label: "Site audit", icon: ShieldAlert, testid: "nav-audit" },
    { to: "/app/content", label: "Content / AEO", icon: Bot, testid: "nav-content" },
    { to: "/app/cannibalization", label: "Cannibalization", icon: Sparkles, testid: "nav-cannib" },
    { to: "/app/backlinks", label: "Backlinks", icon: Link2, testid: "nav-backlinks" },
    { to: "/app/competitors", label: "Competitors", icon: LineChart, testid: "nav-competitors" },
    { to: "/app/local-seo", label: "Local SEO", icon: MapPin, testid: "nav-local-seo" },
    { to: "/app/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
    { to: "/app/referrals", label: "Referrals", icon: Gift, testid: "nav-referrals" },
    { to: "/app/glossary", label: "Metrics Glossary", icon: BookOpen, testid: "nav-glossary" },
    { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [projects, setProjects] = useState([]);
    const [project, setProject] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        client.get("/projects").then((r) => {
            setProjects(r.data);
            const stored = (() => { try { return JSON.parse(localStorage.getItem("rf_project")); } catch { return null; } })();
            const chosen = r.data.find((p) => p.id === stored?.id) || r.data[0];
            setProject(chosen);
            localStorage.setItem("rf_project", JSON.stringify(chosen));
            window.dispatchEvent(new Event("rf_project_changed"));
        });
    }, []);

    const selectProject = (p) => {
        setProject(p);
        localStorage.setItem("rf_project", JSON.stringify(p));
        window.dispatchEvent(new Event("rf_project_changed"));
    };

    const [creating, setCreating] = useState(false);
    const [modalMode, setModalMode] = useState(null);   // "create" | "edit" | null
    const openCreateModal = () => setModalMode("create");
    const openEditModal = () => setModalMode("edit");

    const onCreated = (p) => {
        setProjects((prev) => [...prev, p]);
        selectProject(p);
    };
    const onUpdated = (p) => {
        setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)));
        if (project?.id === p.id) selectProject(p);
    };
    const onDeleted = (p) => {
        const next = projects.filter((x) => x.id !== p.id);
        setProjects(next);
        if (project?.id === p.id) {
            const fallback = next[0] || null;
            if (fallback) selectProject(fallback);
            else localStorage.removeItem("rf_project");
        }
    };

    return (
        <div className="min-h-screen flex bg-background text-foreground">
            {/* Sidebar */}
            <aside
                className={`${mobileOpen ? "block" : "hidden"} lg:block fixed lg:sticky top-0 z-40 lg:z-auto w-64 h-screen border-r border-border bg-card/40 flex-col`}
                data-testid="app-sidebar"
            >
                <div className="h-24 border-b border-border flex items-center px-5 justify-between">
                    <Link to="/app" className="flex items-center gap-2">
                        <Logo className="h-20 w-auto" />
                    </Link>
                    <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)} data-testid="sidebar-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <nav className="p-3 space-y-0.5">
                    {NAV.filter((item) => {
                        // Hide Settings/Billing tab for restricted team members (role='member')
                        if (item.testid === "nav-settings" && user?.is_team_member && !user?.can_manage_billing) return false;
                        return true;
                    }).map((item) => (
                        <NavLink
                            key={item.to} to={item.to} end={item.end}
                            data-testid={item.testid}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 ${isActive ? "border-primary text-foreground bg-accent/40" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/20"}`}
                        >
                            <item.icon className="w-4 h-4" strokeWidth={1.5} />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="mt-auto p-4 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-1">Signed in as</div>
                    <div className="text-sm truncate mb-1" data-testid="sidebar-user-name">{user?.name}</div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-block text-xs font-mono px-1.5 py-0.5 border ${user?.is_admin ? "border-primary text-primary" : user?.tier === "agency" ? "border-primary text-primary" : user?.tier === "solo" || user?.tier === "pro" ? "border-primary/60 text-primary" : "border-border text-muted-foreground"}`} data-testid="tier-badge">
                            {user?.is_admin ? "ADMIN" : (user?.tier || "free").toUpperCase()}
                        </span>
                        {!user?.is_admin && user?.tier === "free" && (
                            <Link to="/pricing" data-testid="sidebar-upgrade-link" className="text-xs text-primary hover:underline flex items-center gap-1">
                                <Zap className="w-3 h-3" /> Upgrade
                            </Link>
                        )}
                    </div>
                    {user?.tier === "pro" && (
                        <button data-testid="sidebar-manage-billing"
                                onClick={async () => {
                                    try {
                                        const r = await (await import("@/lib/api")).default.post("/billing/portal", { return_url: window.location.href });
                                        window.location.href = r.data.portal_url;
                                    } catch (e) { toast.error(e?.response?.data?.detail || "Portal unavailable"); }
                                }}
                                className="w-full text-left text-xs text-muted-foreground hover:text-foreground flex items-center gap-2 mb-2">
                            <CreditCard className="w-3.5 h-3.5" /> Manage billing
                        </button>
                    )}
                    <button
                        data-testid="sidebar-logout-btn"
                        onClick={() => { logout(); nav("/"); }}
                        className="w-full text-left text-xs text-muted-foreground hover:text-destructive flex items-center gap-2"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header className="h-16 border-b border-border flex items-center justify-between px-6 bg-background/70 backdrop-blur-xl sticky top-0 z-30">
                    <div className="flex items-center gap-3">
                        <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(true)} data-testid="sidebar-open">
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="overline hidden sm:block">Project</div>
                        <select
                            data-testid="project-selector"
                            value={project?.id || ""}
                            onChange={(e) => selectProject(projects.find((p) => p.id === e.target.value))}
                            className="bg-input border border-border px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-primary"
                        >
                            {projects.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.domain}`}</option>)}
                        </select>
                        <button
                            data-testid="new-project-btn"
                            onClick={openCreateModal}
                            disabled={creating}
                            title="Create a new project"
                            className="border border-border hover:bg-primary/10 hover:border-primary hover:text-primary px-2 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span className="hidden md:inline text-xs font-mono">New</span>
                        </button>
                        {project && (
                            <button
                                data-testid="edit-project-btn"
                                onClick={openEditModal}
                                title="Edit or delete this project"
                                className="border border-border hover:bg-primary/10 hover:border-primary hover:text-primary px-2 py-1.5 text-sm flex items-center gap-1"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <UsageMeter />
                        <AccessibilityZoom />
                        <div className="text-xs text-muted-foreground font-mono hidden sm:block">
                            {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </div>
                    </div>
                </header>
                {user && !user.email_verified && !user.is_admin && !user.is_team_member && user.tier !== "free" && (
                    <VerifyEmailBanner userEmail={user.email} />
                )}
                <main className="p-6 md:p-8" data-testid="app-main">
                    <Outlet />
                </main>
            </div>
            <AnnualUpsellModal />
            <UpgradeNagModal />
            <OnboardingTour />
            <ProjectManagerModal
                open={modalMode !== null}
                onOpenChange={(v) => !v && setModalMode(null)}
                mode={modalMode || "create"}
                project={modalMode === "edit" ? project : null}
                onCreated={onCreated}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
            />
        </div>
    );
}


function VerifyEmailBanner({ userEmail }) {
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const resend = async () => {
        setSending(true);
        try {
            await client.post("/auth/resend-verification");
            setSent(true);
            toast.success("Verification email sent — check your inbox");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send verification email");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            data-testid="verify-email-banner"
            className="border-b border-warning/40 bg-warning/10 text-warning-foreground px-6 py-2.5 flex items-center gap-3 flex-wrap"
        >
            <MailWarning className="w-4 h-4 text-warning shrink-0" />
            <div className="text-sm flex-1 min-w-0">
                <span className="text-warning font-medium">Confirm your email</span>
                <span className="text-muted-foreground ml-2">
                    Credit-spending actions are paused until <span className="font-mono text-foreground">{userEmail}</span> is verified.
                </span>
            </div>
            <button
                data-testid="verify-resend-btn"
                onClick={resend}
                disabled={sending || sent}
                className="border border-warning/60 text-warning px-3 py-1 text-xs font-mono uppercase tracking-widest hover:bg-warning/10 disabled:opacity-60 flex items-center gap-2"
            >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {sent ? "Sent · check inbox" : "Resend link"}
            </button>
        </div>
    );
}
